import type { EditorPluginName, ViewerPluginName } from '@wix/ricos/schema-next';
import { editorPluginNames, viewerPluginNames } from '@wix/ricos/schema-next';
import React from 'react';

export type PluginSettings = {
  viewer: ViewerPluginName[];
  editor: EditorPluginName[];
};

export type PluginParseResult = PluginSettings & { unknownPlugins: string[] };

const disabledPlaygroundPlugins: EditorPluginName[] = ['ai'];

export const allowedEditorPlugins = editorPluginNames
  .filter(plugin => !disabledPlaygroundPlugins.includes(plugin))
  .sort();

export const defaultPluginSettings: PluginSettings = {
  viewer: [...viewerPluginNames],
  editor: allowedEditorPlugins,
};

export const useParsedPlugins = (pluginsString?: string): PluginSettings => {
  return React.useMemo(() => {
    const { unknownPlugins, ...rest } = parsePlugins(pluginsString);
    console.error('Unknown plugins passed to ricos widget: ', unknownPlugins.join(', '));
    return rest;
  }, [pluginsString]);
};

const pluginsNone = { viewer: [], editor: [], unknownPlugins: [] };

export const parsePlugins = (
  plugins: string | undefined
): PluginSettings & { unknownPlugins: string[] } => {
  if (plugins === undefined) {
    return { ...defaultPluginSettings, unknownPlugins: [] };
  }
  if (plugins.toUpperCase() === 'NONE' || plugins === '') {
    return pluginsNone;
  }
  // using . as a separator to have nicer query
  const separator = plugins.includes('.') ? '.' : ',';
  return plugins
    .split(separator)
    .map(p => p.trim())
    .reduce<PluginSettings & { unknownPlugins: string[] }>(
      (acc, maybePluginName) => {
        const editorPlugin = allowedEditorPlugins.find(
          plugin => plugin.toLowerCase() === maybePluginName.toLowerCase()
        );
        const viewerPlugin = viewerPluginNames.find(
          plugin => plugin.toLowerCase() === maybePluginName.toLowerCase()
        );
        if (editorPlugin) {
          acc.editor.push(editorPlugin);
        }
        if (viewerPlugin) {
          acc.viewer.push(viewerPlugin);
        }
        if (!editorPlugin && !viewerPlugin) {
          acc.unknownPlugins.push(maybePluginName);
        }
        return acc;
      },
      { viewer: [], editor: [], unknownPlugins: [] }
    );
};
